import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiCalendar, FiClock, FiUser, FiPhone, FiCheck, FiX, FiEdit, FiEye, FiFilter, FiSearch, FiPlus } from 'react-icons/fi';
import { FaCar, FaTools, FaClipboardCheck } from 'react-icons/fa';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import { leadsAPI } from '../../lib/supabase';

const ServiceAppointmentManager = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [serviceBays, setServiceBays] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [searchResults, setSearchResults] = useState({ leads: [], inquiries: [] });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  const [newAppointment, setNewAppointment] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    vehicle_year: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_vin: '',
    vehicle_license: '',
    appointment_date: new Date().toISOString().split('T')[0],
    appointment_time: '09:00',
    service_type_id: '',
    service_description: '',
    customer_concerns: '',
    estimated_cost: 0
  });
  
  const [stats, setStats] = useState({
    todayTotal: 0,
    checkedIn: 0,
    inProgress: 0,
    completed: 0
  });

  useEffect(() => {
    loadAppointments();
    loadTechnicians();
    loadServiceBays();
    loadServiceTypes();
  }, [selectedDate, filterStatus]);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('service_appointments')
        .select('*')
        .eq('appointment_date', selectedDate)
        .order('appointment_time');

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data, error } = await query;
      if (error) throw error;

      console.log('Loaded appointments for date:', selectedDate, data);
      setAppointments(data || []);
      calculateStats(data || []);
    } catch (err) {
      console.error('Error loading appointments:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTechnicians = async () => {
    try {
      const { data, error } = await supabase
        .from('service_staff')
        .select('*')
        .eq('active', true)
        .in('role', ['technician', 'advisor']);
      
      if (error) throw error;
      setTechnicians(data || []);
    } catch (err) {
      console.error('Error loading technicians:', err);
    }
  };

  const loadServiceBays = async () => {
    try {
      const { data, error } = await supabase
        .from('service_bays')
        .select('*')
        .eq('active', true)
        .order('bay_number');
      
      if (error) throw error;
      setServiceBays(data || []);
    } catch (err) {
      console.error('Error loading bays:', err);
    }
  };

  const loadServiceTypes = async () => {
    try {
      const { data, error } = await supabase
        .from('service_types')
        .select('*')
        .eq('active', true)
        .order('name');
      
      if (error) throw error;
      setServiceTypes(data || []);
    } catch (err) {
      console.error('Error loading service types:', err);
    }
  };

  const searchCustomers = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults({ leads: [], inquiries: [] });
      return;
    }

    try {
      // Search leads
      const { data: leads } = await supabase
        .from('leads')
        .select('*')
        .or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`)
        .limit(5);

      // Search contact inquiries
      const { data: inquiries } = await supabase
        .from('contact_inquiries')
        .select('*')
        .or(`name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`)
        .limit(5);

      setSearchResults({
        leads: leads || [],
        inquiries: inquiries || []
      });
    } catch (err) {
      console.error('Error searching customers:', err);
    }
  };

  const handleCustomerSelect = (customer, type) => {
    setSelectedCustomer({ ...customer, type });
    
    if (type === 'lead') {
      setNewAppointment({
        ...newAppointment,
        customer_name: `${customer.first_name || ''} ${customer.last_name || ''}`.trim(),
        customer_email: customer.email || '',
        customer_phone: customer.phone || ''
      });
    } else {
      setNewAppointment({
        ...newAppointment,
        customer_name: customer.name || '',
        customer_email: customer.email || '',
        customer_phone: customer.phone || ''
      });
    }
    
    setCustomerSearch('');
    setSearchResults({ leads: [], inquiries: [] });
  };

  const handleCreateAppointment = async () => {
    try {
      // Validate required fields
      if (!newAppointment.customer_name || !newAppointment.customer_phone) {
        alert('Please fill in customer name and phone number');
        return;
      }

      // Generate appointment number
      const appointmentNumber = `SVC${Date.now().toString().slice(-8)}`;
      
      // Prepare appointment data, handling empty service_type_id
      const appointmentData = {
        appointment_number: appointmentNumber,
        customer_name: newAppointment.customer_name,
        customer_email: newAppointment.customer_email || null,
        customer_phone: newAppointment.customer_phone,
        vehicle_year: newAppointment.vehicle_year || null,
        vehicle_make: newAppointment.vehicle_make || null,
        vehicle_model: newAppointment.vehicle_model || null,
        vehicle_vin: newAppointment.vehicle_vin || null,
        vehicle_license: newAppointment.vehicle_license || null,
        appointment_date: newAppointment.appointment_date,
        appointment_time: newAppointment.appointment_time,
        service_type_id: newAppointment.service_type_id || null, // Convert empty string to null
        service_description: newAppointment.service_description || null,
        customer_concerns: newAppointment.customer_concerns || null,
        estimated_cost: newAppointment.estimated_cost || 0,
        status: 'scheduled',
        created_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('service_appointments')
        .insert(appointmentData)
        .select();

      if (error) throw error;

      alert('Service appointment created successfully!');
      setShowAddModal(false);
      
      // Set the selected date to the appointment date to view it
      setSelectedDate(newAppointment.appointment_date);
      
      // Reload appointments immediately
      await loadAppointments();
      
      setNewAppointment({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        vehicle_year: '',
        vehicle_make: '',
        vehicle_model: '',
        vehicle_vin: '',
        vehicle_license: '',
        appointment_date: new Date().toISOString().split('T')[0],
        appointment_time: '09:00',
        service_type_id: '',
        service_description: '',
        customer_concerns: '',
        estimated_cost: 0
      });
      setSelectedCustomer(null);
      loadAppointments();
    } catch (err) {
      console.error('Error creating appointment:', err);
      alert('Failed to create appointment: ' + err.message);
    }
  };

  const calculateStats = (appointmentData) => {
    setStats({
      todayTotal: appointmentData.length,
      checkedIn: appointmentData.filter(a => a.status === 'checked_in').length,
      inProgress: appointmentData.filter(a => a.status === 'in_progress').length,
      completed: appointmentData.filter(a => a.status === 'completed').length
    });
  };

  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    try {
      const updateData = { 
        status: newStatus,
        updated_at: new Date().toISOString()
      };

      if (newStatus === 'checked_in') {
        updateData.checked_in_at = new Date().toISOString();
      } else if (newStatus === 'completed') {
        updateData.completed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('service_appointments')
        .update(updateData)
        .eq('id', appointmentId);

      if (error) throw error;

      loadAppointments();
      setShowDetailModal(false);
    } catch (err) {
      console.error('Error updating appointment:', err);
      alert('Failed to update appointment status');
    }
  };

  const assignTechnician = async (appointmentId, technicianId) => {
    try {
      const { error } = await supabase
        .from('service_appointments')
        .update({ 
          assigned_technician_id: technicianId,
          updated_at: new Date().toISOString()
        })
        .eq('id', appointmentId);

      if (error) throw error;
      loadAppointments();
    } catch (err) {
      console.error('Error assigning technician:', err);
    }
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-green-100 text-green-800',
      checked_in: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-orange-100 text-orange-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      no_show: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredAppointments = appointments.filter(apt => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      apt.customer_name?.toLowerCase().includes(search) ||
      apt.customer_phone?.includes(search) ||
      apt.vehicle_make?.toLowerCase().includes(search) ||
      apt.vehicle_model?.toLowerCase().includes(search) ||
      apt.vehicle_license?.toLowerCase().includes(search) ||
      apt.appointment_number?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Service Appointments</h2>
          <p className="text-gray-600">Manage daily service appointments and work orders</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold flex items-center gap-2"
        >
          <SafeIcon icon={FiPlus} className="w-5 h-5" />
          Add Appointment
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Today's Appointments</p>
              <p className="text-2xl font-bold text-gray-900">{stats.todayTotal}</p>
            </div>
            <SafeIcon icon={FiCalendar} className="w-8 h-8 text-blue-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Checked In</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.checkedIn}</p>
            </div>
            <SafeIcon icon={FaClipboardCheck} className="w-8 h-8 text-yellow-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-orange-600">{stats.inProgress}</p>
            </div>
            <SafeIcon icon={FaTools} className="w-8 h-8 text-orange-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
            </div>
            <SafeIcon icon={FiCheck} className="w-8 h-8 text-green-500" />
          </div>
        </motion.div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          />
          
          <div className="flex-1">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by customer, vehicle, or appointment number..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg font-medium ${
                filterStatus === 'all' 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterStatus('scheduled')}
              className={`px-4 py-2 rounded-lg font-medium ${
                filterStatus === 'scheduled' 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Scheduled
            </button>
            <button
              onClick={() => setFilterStatus('checked_in')}
              className={`px-4 py-2 rounded-lg font-medium ${
                filterStatus === 'checked_in' 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Checked In
            </button>
            <button
              onClick={() => setFilterStatus('in_progress')}
              className={`px-4 py-2 rounded-lg font-medium ${
                filterStatus === 'in_progress' 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              In Progress
            </button>
          </div>
        </div>
      </div>

      {/* Appointments Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : filteredAppointments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No appointments found for selected criteria
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Technician</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAppointments.map((appointment) => (
                  <tr key={appointment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatTime(appointment.appointment_time)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {appointment.duration_minutes} min
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {appointment.customer_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {appointment.customer_phone}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {appointment.vehicle_year} {appointment.vehicle_make} {appointment.vehicle_model}
                      </div>
                      <div className="text-xs text-gray-500">
                        {appointment.vehicle_license}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {appointment.service_types?.name}
                      </div>
                      {appointment.customer_concerns && (
                        <div className="text-xs text-gray-500 truncate max-w-xs">
                          {appointment.customer_concerns}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {appointment.assigned_technician_id ? (
                        <div className="text-sm text-gray-900">
                          {appointment.service_staff?.name}
                        </div>
                      ) : (
                        <select
                          onChange={(e) => assignTechnician(appointment.id, e.target.value)}
                          className="text-sm border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="">Assign...</option>
                          {technicians.filter(t => t.role === 'technician').map(tech => (
                            <option key={tech.id} value={tech.id}>
                              {tech.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(appointment.status)}`}>
                        {appointment.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setSelectedAppointment(appointment);
                            setShowDetailModal(true);
                          }}
                          className="text-primary-600 hover:text-primary-700"
                        >
                          <SafeIcon icon={FiEye} className="w-4 h-4" />
                        </button>
                        
                        {appointment.status === 'scheduled' && (
                          <button
                            onClick={() => updateAppointmentStatus(appointment.id, 'checked_in')}
                            className="text-green-600 hover:text-green-700"
                            title="Check In"
                          >
                            <SafeIcon icon={FiCheck} className="w-4 h-4" />
                          </button>
                        )}
                        
                        {appointment.status === 'checked_in' && (
                          <button
                            onClick={() => updateAppointmentStatus(appointment.id, 'in_progress')}
                            className="text-orange-600 hover:text-orange-700"
                            title="Start Service"
                          >
                            <SafeIcon icon={FaTools} className="w-4 h-4" />
                          </button>
                        )}
                        
                        {appointment.status === 'in_progress' && (
                          <button
                            onClick={() => updateAppointmentStatus(appointment.id, 'completed')}
                            className="text-green-600 hover:text-green-700"
                            title="Complete"
                          >
                            <SafeIcon icon={FiCheck} className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    Appointment Details
                  </h3>
                  <p className="text-sm text-gray-600">
                    {selectedAppointment.appointment_number}
                  </p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Customer Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Customer Information</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Name:</span>
                      <span className="ml-2 font-medium">{selectedAppointment.customer_name}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Phone:</span>
                      <span className="ml-2 font-medium">{selectedAppointment.customer_phone}</span>
                    </div>
                    {selectedAppointment.customer_email && (
                      <div className="col-span-2">
                        <span className="text-gray-600">Email:</span>
                        <span className="ml-2 font-medium">{selectedAppointment.customer_email}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Vehicle Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Vehicle Information</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">Vehicle:</span>
                      <span className="ml-2 font-medium">
                        {selectedAppointment.vehicle_year} {selectedAppointment.vehicle_make} {selectedAppointment.vehicle_model}
                      </span>
                    </div>
                    {selectedAppointment.vehicle_vin && (
                      <div>
                        <span className="text-gray-600">VIN:</span>
                        <span className="ml-2 font-medium">{selectedAppointment.vehicle_vin}</span>
                      </div>
                    )}
                    {selectedAppointment.vehicle_license && (
                      <div>
                        <span className="text-gray-600">License:</span>
                        <span className="ml-2 font-medium">{selectedAppointment.vehicle_license}</span>
                      </div>
                    )}
                    {selectedAppointment.mileage && (
                      <div>
                        <span className="text-gray-600">Mileage:</span>
                        <span className="ml-2 font-medium">{selectedAppointment.mileage.toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Service Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Service Details</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-600">Service:</span>
                      <span className="ml-2 font-medium">{selectedAppointment.service_types?.name}</span>
                    </div>
                    {selectedAppointment.customer_concerns && (
                      <div>
                        <span className="text-gray-600">Customer Concerns:</span>
                        <p className="mt-1 text-gray-900">{selectedAppointment.customer_concerns}</p>
                      </div>
                    )}
                    {selectedAppointment.service_description && (
                      <div>
                        <span className="text-gray-600">Additional Services:</span>
                        <p className="mt-1 text-gray-900">{selectedAppointment.service_description}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Transportation */}
                {(selectedAppointment.needs_loaner || selectedAppointment.needs_shuttle || selectedAppointment.waiting) && (
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Transportation</h4>
                    <div className="space-y-1 text-sm">
                      {selectedAppointment.waiting && (
                        <div className="text-yellow-800">• Customer is waiting</div>
                      )}
                      {selectedAppointment.needs_shuttle && (
                        <div className="text-yellow-800">• Shuttle service requested</div>
                      )}
                      {selectedAppointment.needs_loaner && (
                        <div className="text-yellow-800">• Loaner vehicle requested</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Close
                  </button>
                  
                  {selectedAppointment.status === 'scheduled' && (
                    <button
                      onClick={() => updateAppointmentStatus(selectedAppointment.id, 'checked_in')}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                    >
                      Check In Customer
                    </button>
                  )}
                  
                  {selectedAppointment.status === 'checked_in' && (
                    <button
                      onClick={() => updateAppointmentStatus(selectedAppointment.id, 'in_progress')}
                      className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg"
                    >
                      Start Service
                    </button>
                  )}
                  
                  {selectedAppointment.status === 'in_progress' && (
                    <button
                      onClick={() => updateAppointmentStatus(selectedAppointment.id, 'completed')}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                    >
                      Complete Service
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Add Appointment Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Add Service Appointment</h3>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setSelectedCustomer(null);
                    setCustomerSearch('');
                    setSearchResults({ leads: [], inquiries: [] });
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Customer Search */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Search Existing Customer
                  </label>
                  <input
                    type="text"
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      searchCustomers(e.target.value);
                    }}
                    placeholder="Search by name, phone, or email..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                  
                  {/* Search Results */}
                  {(searchResults.leads.length > 0 || searchResults.inquiries.length > 0) && (
                    <div className="mt-2 border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                      {searchResults.leads.map(lead => (
                        <div
                          key={lead.id}
                          onClick={() => handleCustomerSelect(lead, 'lead')}
                          className="p-2 hover:bg-gray-50 cursor-pointer border-b"
                        >
                          <div className="font-medium">
                            {lead.first_name} {lead.last_name}
                            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Lead</span>
                          </div>
                          <div className="text-sm text-gray-600">
                            {lead.phone} • {lead.email}
                          </div>
                        </div>
                      ))}
                      {searchResults.inquiries.map(inquiry => (
                        <div
                          key={inquiry.id}
                          onClick={() => handleCustomerSelect(inquiry, 'inquiry')}
                          className="p-2 hover:bg-gray-50 cursor-pointer border-b"
                        >
                          <div className="font-medium">
                            {inquiry.name}
                            <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Inquiry</span>
                          </div>
                          <div className="text-sm text-gray-600">
                            {inquiry.phone} • {inquiry.email}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedCustomer && (
                    <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                      <span className="text-sm text-blue-800">
                        Selected: {selectedCustomer.type === 'lead' 
                          ? `${selectedCustomer.first_name} ${selectedCustomer.last_name}`
                          : selectedCustomer.name
                        }
                      </span>
                    </div>
                  )}
                </div>

                {/* Customer Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Customer Name *
                    </label>
                    <input
                      type="text"
                      value={newAppointment.customer_name}
                      onChange={(e) => setNewAppointment({...newAppointment, customer_name: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={newAppointment.customer_phone}
                      onChange={(e) => setNewAppointment({...newAppointment, customer_phone: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={newAppointment.customer_email}
                      onChange={(e) => setNewAppointment({...newAppointment, customer_email: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                {/* Vehicle Information */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Year
                    </label>
                    <input
                      type="text"
                      value={newAppointment.vehicle_year}
                      onChange={(e) => setNewAppointment({...newAppointment, vehicle_year: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Make
                    </label>
                    <input
                      type="text"
                      value={newAppointment.vehicle_make}
                      onChange={(e) => setNewAppointment({...newAppointment, vehicle_make: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Model
                    </label>
                    <input
                      type="text"
                      value={newAppointment.vehicle_model}
                      onChange={(e) => setNewAppointment({...newAppointment, vehicle_model: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      VIN
                    </label>
                    <input
                      type="text"
                      value={newAppointment.vehicle_vin}
                      onChange={(e) => setNewAppointment({...newAppointment, vehicle_vin: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      License Plate
                    </label>
                    <input
                      type="text"
                      value={newAppointment.vehicle_license}
                      onChange={(e) => setNewAppointment({...newAppointment, vehicle_license: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                {/* Appointment Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Appointment Date *
                    </label>
                    <input
                      type="date"
                      value={newAppointment.appointment_date}
                      onChange={(e) => setNewAppointment({...newAppointment, appointment_date: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Appointment Time *
                    </label>
                    <input
                      type="time"
                      value={newAppointment.appointment_time}
                      onChange={(e) => setNewAppointment({...newAppointment, appointment_time: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Service Type
                    </label>
                    <select
                      value={newAppointment.service_type_id}
                      onChange={(e) => setNewAppointment({...newAppointment, service_type_id: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Select Service Type</option>
                      {serviceTypes.map(type => (
                        <option key={type.id} value={type.id}>
                          {type.name} - ${type.price}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estimated Cost
                    </label>
                    <input
                      type="number"
                      value={newAppointment.estimated_cost}
                      onChange={(e) => setNewAppointment({...newAppointment, estimated_cost: parseFloat(e.target.value) || 0})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                {/* Service Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Description
                  </label>
                  <textarea
                    value={newAppointment.service_description}
                    onChange={(e) => setNewAppointment({...newAppointment, service_description: e.target.value})}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Describe the service needed..."
                  />
                </div>

                {/* Customer Concerns */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Concerns
                  </label>
                  <textarea
                    value={newAppointment.customer_concerns}
                    onChange={(e) => setNewAppointment({...newAppointment, customer_concerns: e.target.value})}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="Any specific concerns from the customer..."
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setSelectedCustomer(null);
                      setCustomerSearch('');
                      setSearchResults({ leads: [], inquiries: [] });
                    }}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateAppointment}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    Create Appointment
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ServiceAppointmentManager;
